// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx

import { AskV2ReviewCompare } from "./AskV2ReviewCompare";
import { AskV2ReviewCompareStatus } from "./AskV2ReviewCompareStatus";
import {
  type useAskPunttReview,
  useSetAskPunttReviewChangeReviewed,
} from "./useAskPunttReview";
import { type useAskV2ReviewVersionCompare } from "./useAskV2ReviewVersionCompare";
import { useCompareKeyboard } from "./useCompareKeyboard";
import { COMPARE_CHANGE_PREFIX, comparePinTone } from "./versionChanges";

import {
  AskPunttArtworkViewer,
  type HumanCommentSubmitInput,
  type useReviewArtworkFocus,
} from "../../components/ask-puntt-review";
import { LinkedArtworkViewProvider } from "../../components/ask-puntt-review/linkedArtworkView";
import { type mapReviewFilesToAssets } from "../../components/ask-puntt-review/mapReviewAssets";

type Review = NonNullable<
  NonNullable<ReturnType<typeof useAskPunttReview>["data"]>["review"]
>;

type ArtworkFocus = ReturnType<typeof useReviewArtworkFocus>;

type AskV2ReviewCompareLayoutProps = {
  review: Review;
  compare: ReturnType<typeof useAskV2ReviewVersionCompare>;
  artworkFiles: ReturnType<typeof mapReviewFilesToAssets>;
  artworkPaneRef: ArtworkFocus["artworkPaneRef"];
  setArtworkHeaderControls: ArtworkFocus["setArtworkHeaderControls"];
  selectedCommentId: string | null;
  commentNumbers: Record<string, number>;
  currentUserId?: string;
  /** A finding's pin was clicked on the open review's artwork. */
  onOpenComment: (commentId: string) => void;
  onArtworkBackgroundClick: () => void;
  onSubmitHumanComment?: (input: HumanCommentSubmitInput) => Promise<void>;
};

/**
 * The artwork column under its toolbar: the open review's viewer, and while
 * comparing, the compare status strip, the compared version beside it and
 * the comparison's keyboard shortcuts.
 */
export function AskV2ReviewCompareLayout({
  review,
  compare,
  artworkFiles,
  artworkPaneRef,
  setArtworkHeaderControls,
  selectedCommentId,
  commentNumbers,
  currentUserId,
  onOpenComment,
  onArtworkBackgroundClick,
  onSubmitHumanComment,
}: AskV2ReviewCompareLayoutProps) {
  const {
    isComparing,
    compareReviewId,
    comparison,
    changeAt,
    steppedRegion,
    steppedPin,
    selectedComment,
    newerReview,
    reviewedNumbers,
  } = compare;
  const setChangeReviewed = useSetAskPunttReviewChangeReviewed();

  useCompareKeyboard(isComparing, {
    onStep: compare.stepChange,
    onToggleReviewed: () => {
      if (!steppedRegion || !newerReview || !comparison) return;
      setChangeReviewed.mutate({
        reviewId: newerReview.reviewId,
        comparisonId: comparison.id,
        number: steppedRegion.number,
        reviewed: !reviewedNumbers.has(steppedRegion.number),
      });
    },
    onExit: () => compare.setComparing(false),
  });

  // why keys, not a CSS order: the earlier version goes on the left, and
  // keyed children move without remounting the open viewer (its zoom and
  // scroll), while the reading order still matches what is on screen.
  const comparedPane =
    isComparing && compareReviewId ? (
      <AskV2ReviewCompare
        key="compared"
        placement={compare.openIsEarlier ? "right" : "left"}
        reviewId={compareReviewId}
        selectedComment={steppedPin ?? selectedComment}
        selectedNumber={
          steppedRegion
            ? steppedRegion.number
            : selectedCommentId
              ? commentNumbers[selectedCommentId]
              : undefined
        }
        currentUserId={currentUserId}
        side={compare.comparedSide}
        changes={compare.comparedChanges}
        // Only a changed window answers here: this version's own
        // findings are not the open review's, and have no card.
        onPinClick={compare.stepToPin}
      />
    ) : null;
  const currentPane = (
    <div
      key="current"
      className="flex min-h-0 min-w-0 flex-1 flex-col"
      data-testid="ask-v2-review-artwork-current"
    >
      <div className="min-h-0 flex-1">
        <AskPunttArtworkViewer
          ref={artworkPaneRef}
          reviewId={review.reviewId}
          title={review.title}
          files={artworkFiles}
          comments={compare.viewerComments}
          commentNumbers={compare.viewerNumbers}
          currentUserId={currentUserId}
          activeCommentId={steppedPin?._id ?? selectedCommentId}
          pinTone={comparePinTone(compare.openSide)}
          onHeaderControlsChange={setArtworkHeaderControls}
          onCommentClick={(comment) => {
            // A changed window is not a finding: clicking its pin
            // steps to it, and there is nothing to open in the column.
            if (comment._id.startsWith(COMPARE_CHANGE_PREFIX)) {
              compare.stepToPin(comment);
              return;
            }
            onOpenComment(comment._id);
          }}
          onArtworkBackgroundClick={onArtworkBackgroundClick}
          // why: same as the judge controls — this composer POSTs
          // `createReviewComment` off the transport. Without the
          // handler the viewer falls back to the read-only placement
          // trigger and a bare click no longer drops a draft pin.
          onSubmitHumanComment={onSubmitHumanComment}
        />
      </div>
    </div>
  );

  return (
    <>
      {isComparing && compare.comparedQuery.data ? (
        <AskV2ReviewCompareStatus
          comparison={comparison}
          changeAt={changeAt}
          steppedRegion={steppedRegion}
          hasSelectedComment={
            selectedComment != null && compare.canPlaceFinding
          }
          selectedRegion={compare.selectedRegion}
          pinsShown={compare.pinsShown}
          onShowPins={compare.showPins}
        />
      ) : null}
      <LinkedArtworkViewProvider enabled={isComparing && compare.syncPanes}>
        <div className="flex min-h-0 flex-1">
          {compare.openIsEarlier
            ? [currentPane, comparedPane]
            : [comparedPane, currentPane]}
        </div>
      </LinkedArtworkViewProvider>
    </>
  );
}
